import { Box, Grid, Paper, Stack } from '@mui/material';
import { Divider, Typography } from '@chr/eds-react';
import { useTranslation } from '@chr/online-shared-ui-localizations';

import { CarrierSummary as CarrierSummaryType } from '@/common/apis/dtos';
import { InformationDisplay } from '@/common/components/information-display/InformationDisplay';
import { Section } from '@/common/components/section/Section';
import { removeExtraSpace } from '@/common/utilities/display-utilities';
import { TransportMode } from '@/common/constants/booking-types';

import PortSummary from './PortSummary';

interface CarrierSummaryProps {
  carrierSummaries: CarrierSummaryType[];
  transportMode: TransportMode
}

export const CarrierSummary = ({ transportMode, carrierSummaries }: CarrierSummaryProps) => {
  const translate = useTranslation();

  const isOceanMode = transportMode === TransportMode.OCEAN;

  if (carrierSummaries.length === 0) {
    return null;
  }

  return (
    <Section
      data-testid="carrier-summary"
      headerParentSx={{
        paddingTop: '7px',
        paddingBottom: '7px',
      }}
      header={<Typography variant="heading300">{translate('Carrier Summary')}</Typography>}
    >
      <Paper elevation={0}>
        {carrierSummaries?.map((carrier, idx) => {
          const originPartyCode = carrier.originPortLocation?.partyCode ?? '';
          const destinationPartyCode = carrier.destinationPortLocation?.partyCode ?? '';
          const originDestKey = `${originPartyCode}${destinationPartyCode}` || idx;

          return (
            <Paper
              key={originDestKey}
              elevation={0}
              variant="outlined"
              sx={{ m: 2 }}
            >
              <Box justifyItems={'center'} sx={{ ml: 2, mt: 2 }}>
                {isOceanMode ? (
                  <Typography variant={"subtitle100"} data-testid="vessel-name">
                    {`${translate('Vessel')} ${removeExtraSpace(
                      carrier.vesselName,
                      '-'
                    )}`}
                  </Typography>
                ) : (
                  <Typography variant={"subtitle100"} data-testid="flight-number">
                    {`${translate('Flight')} ${removeExtraSpace(
                      carrier.voyageNumber,
                      '-'
                    )}`}
                  </Typography>
                )}
                <Box sx={{ marginY: 2, borderBottomWidth: 2, mr: 2 }}>
                  <Divider emphasis="low" />
                </Box>
              </Box>
              <Grid container mb={2}>
                <Grid item md={3} xs={12} p={2}>
                  <Stack gap={2}>
                    <InformationDisplay
                      data-testid="voyage-number"
                      label={
                        isOceanMode
                          ? translate('Voyage')
                          : translate('Itinerary')
                      }
                      text={removeExtraSpace(carrier.voyageNumber, '-')}
                    />
                  </Stack>
                </Grid>
                <Grid item md={9} xs={12}>
                  <Box
                    sx={{ backgroundColor: 'grey.50', mr: 2, borderRadius: 1 }}
                  >
                    <Stack
                      direction={{ xs: 'column', sm: 'row' }}
                      divider={
                        <Box>
                          <Divider orientation="vertical" emphasis="low" />
                        </Box>
                      }
                    >
                      <PortSummary
                        port={'Origin'}
                        carrierSummary={carrier}
                        transportMode={transportMode}
                      />
                      <PortSummary
                        port={'Destination'}
                        carrierSummary={carrier}
                        transportMode={transportMode}
                      />
                    </Stack>
                  </Box>
                </Grid>
              </Grid>
            </Paper>
          );
        })}
      </Paper>
    </Section>
  );
};
